import { A } from '@adornis/base/env-info.js';
import { type Maybe } from '@adornis/base/utilTypes.js';
import { getByID, subscribeByID } from '@adornis/baseql/operations/mongo.js';
import { RXController } from '@adornis/chemistry/controllers/RXController.js';
import { css } from '@adornis/chemistry/directives/css.js';
import '@adornis/chemistry/elements/components/x-icon.js';
import '@adornis/dragdrop/x-drag-drop.js';
import { FormController } from '@adornis/forms/x-form-controller.js';
import '@adornis/popover/x-tooltip.js';
import { goTo } from '@adornis/router/client/open-href.js';
import { routingState } from '@adornis/router/client/routing-state.js';
import { type ValidationError } from '@adornis/validation/errors/ValidationError.js';
import { LearningContentAPI } from '@chancenwerk/campus-learning-content/api/LearningContentAPI.js';
import { CampusExercise } from '@chancenwerk/campus-learning-content/entities/CampusExercise.js';
import { CampusLesson } from '@chancenwerk/campus-learning-content/entities/CampusLesson.js';
import { CampusSection } from '@chancenwerk/campus-learning-content/entities/CampusSection.js';
import { CampusSectionFilter } from '@chancenwerk/campus-learning-content/entities/CampusSectionFilter.js';
import '@chancenwerk/components/chancen-component-card-title.js';
import '@chancenwerk/components/chancen-component-context-menu.js';
import { ExerciseType } from '@chancenwerk/db/enums/enums.js';
import { ChancenPermission } from '@chancenwerk/db/permissions.js';
import '@chancenwerk/design/components/cr-button.js';
import '@chancenwerk/design/components/cr-checkbox.js';
import { ChromiumDialog } from '@chancenwerk/design/components/cr-dialog.js';
import '@chancenwerk/design/components/cr-flex.js';
import '@chancenwerk/design/components/cr-icon.js';
import '@chancenwerk/design/components/cr-input.js';
import { CrPage } from '@chancenwerk/design/components/cr-page.js';
import { ChromiumSnackbar } from '@chancenwerk/design/components/cr-snackbar.js';
import '@chancenwerk/design/components/cr-tabs.js';
import '@chancenwerk/design/components/fonts.js';
import { html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { repeat } from 'lit/directives/repeat.js';
import { filter, firstValueFrom, of, switchMap, takeUntil } from 'rxjs';
import '../components/campus-component-attachements.js';
import { CampusDialogGenerateQrcode } from '../components/dialogs/campus-dialog-generate-qrcode.js';
import { CampusDialogMoveExercise } from '../components/dialogs/campus-dialog-move-exercise.js';
import { CampusRoutes } from '../routes.js';
import { ALL_EXERCIE_PAGES } from './exercise/index.js';

/**
 * @customElement campus-page-editorial-edit-lesson
 */
@customElement('campus-page-editorial-edit-lesson')
export class CampusPageEditorialEditLesson extends CrPage {
  @property({ attribute: false }) public lessonID = new RXController<Maybe<string>>(this, '');
  @property({ attribute: false }) public area = new RXController(this, '');

  @state() private readonly _sectionParam = new RXController(this, routingState.searchParams.get('section'));
  @state() private readonly _areaParam = new RXController(this, routingState.searchParams.get('area'));

  @state() private readonly _initialSectionOfLesson = new RXController<Maybe<CampusSection>>(this, null);
  @state() private readonly _lesson = new RXController(
    this,
    this.lessonID.observable.pipe(
      switchMap(id => {
        if (!id?.length || id === '') return of(new CampusLesson({}));
        else return subscribeByID(CampusLesson, id)(CampusLesson.allFields);
      }),
    ),
  );

  private readonly _form = new FormController(this, this._lesson.observable, {
    success: lesson => this._handleFormSuccess(lesson),
    error: err => this._handleFormError(err),
  });

  @state() private readonly _currentSectionOfLesson = new RXController<Maybe<CampusSection>>(
    this,
    this._initialSectionOfLesson.value,
  );

  @state() private readonly _isLoading = new RXController(this, false);

  // This is only needed to make exercises reactive.
  // Tried it with field-resolver so we dont have to subscribe in every component but they are not reactive
  @state() public _exercisesOfLesson = new RXController(
    this,
    this._lesson.observable.pipe(
      switchMap(lesson => {
        if (!lesson?.exerciseIDs) return of([]);
        else return subscribeByID(CampusExercise, lesson.exerciseIDs)(CampusExercise.allFields);
      }),
    ),
  );

  @state() private readonly _otherLessonsInSection = new RXController(
    this,
    this._currentSectionOfLesson.observable.pipe(
      filter(Boolean),
      switchMap(section => subscribeByID(CampusLesson, section.lessonIDs ?? [])({ _id: 1, _class: 1, name: 1 })),
    ),
  );

  // To search an exercise
  @state() private readonly _searchText = new RXController(this, '');

  // Error message for the user
  @state() private readonly _errorMsg = new RXController<string>(this, '');

  @state() private _movingExercise = false;

  public constructor() {
    super();

    this.permission = ChancenPermission.campus.EDIT_CONTENT;
    this.backRoute = CampusRoutes.lessons;
  }

  override connectedCallback() {
    super.connectedCallback();

    this._areaParam.observable.pipe(takeUntil(this.disconnected)).subscribe(area => this.area.next(area ?? ''));

    this._lesson.observable
      .pipe(takeUntil(this.disconnected))
      .subscribe(e => (this.label = !e ? 'Lerninhalt erstellen' : 'Lerninhalt bearbeiten'));

    this._sectionParam.observable.pipe(takeUntil(this.disconnected)).subscribe(sectionID => {
      getByID(
        CampusSection,
        sectionID ?? '',
      )(CampusSection.allFields)
        .then(section => {
          this._initialSectionOfLesson.next(section);
        })
        .catch(console.error);
    });

    this._initialSectionOfLesson.observable.pipe(filter(Boolean), takeUntil(this.disconnected)).subscribe(section => {
      this._currentSectionOfLesson.next(section);
      this.area.next(section.area);
    });
  }

  private async _handleFormSuccess(lesson: CampusLesson) {
    if (!this._currentSectionOfLesson.value) {
      this._errorMsg.next('Es wurde kein Themenbereich für diese Lektion ausgewählt');
      return;
    }

    if (
      this._currentSectionOfLesson.value._id &&
      this._initialSectionOfLesson.value?._id !== this._currentSectionOfLesson.value._id
    ) {
      this._movingExercise = true;
    }

    this._isLoading.next(true);

    // Create New Lesson
    if (!lesson._id) {
      const newID = await lesson.create().catch(e => this._errorMsg.next(e.detail));
      this._currentSectionOfLesson.value.lessonIDs = (this._currentSectionOfLesson.value.lessonIDs ?? []).concat([
        lesson._id,
      ]);
      await this._currentSectionOfLesson.value.save();
      this._initialSectionOfLesson.next(this._currentSectionOfLesson.value);
      this.lessonID.next(newID || 'ERROR');

      if (newID) {
        goTo(CampusRoutes.lessons + `/edit/${newID}?section=${this._currentSectionOfLesson.value._id}`);
      }
    } else {
      // Save existing lesson

      if (this._movingExercise) {
        await LearningContentAPI.manipulation.lesson.moveToSection(
          lesson._id,
          this._initialSectionOfLesson.value?._id ?? '',
          this._currentSectionOfLesson.value._id,
        )();
      }

      await lesson.save().catch(e => this._errorMsg.next(e.detail));
    }
    void ChromiumSnackbar.show('Lerninhalt gespeichert');
    this._movingExercise = false;
    this._isLoading.next(false);
  }
  private _handleFormError(e: ValidationError<any>) {
    this._errorMsg.next(e.message);
    this._isLoading.next(false);
  }

  public override content() {
    if (this._isLoading.value || this.area.value === '')
      return html`
        <cr-flex center crossaxis-center space="lg">
          ${this._movingExercise
            ? html`<cr-text>
                Lektion wird von ${this._initialSectionOfLesson.value?.name} zu
                ${this._currentSectionOfLesson.value?.name} verschoben
              </cr-text>`
            : nothing}
          <cr-campus-loader></cr-campus-loader>
        </cr-flex>
      `;
    return html`
      <cr-flex space="md" vertical ${css({ padding: this.spacing.xs })}>
        ${this._errorMsg.value ? html`<cr-text ${css({ color: 'red' })}>${this._errorMsg.value}</cr-text>` : nothing}

        <cr-grid columns=${this.size.mobile ? '1fr' : '1fr 1fr 1fr'} space="md">
          <cr-card space="md" padding="md">
            <chancen-component-card-title>Basis-Informationen</chancen-component-card-title>
            <cr-input ${this._form.field('name')} placeholder="Name des Lerninhalts"></cr-input>
            <cr-dropdown-search-selection
              .value=${this._currentSectionOfLesson.value}
              placeholder="Themenbereich"
              clearable=${true}
              @value-picked=${(e: CustomEvent) => {
                if (!e.detail.value) return;
                this._currentSectionOfLesson.next(e.detail.value);
              }}
              @cleared=${() => {
                this._currentSectionOfLesson.next(null);
              }}
              .renderString=${(item: CampusSection) => {
                return item.area + ' | ' + item.name;
              }}
              .search=${(search: Maybe<string>) =>
                firstValueFrom(
                  LearningContentAPI.subscribeSectionsByFilter(
                    0,
                    20,
                    new CampusSectionFilter({
                      area: this.area.value,
                      search,
                    }),
                  )({
                    area: 1,
                    name: 1,
                    hiddenFromStudents: 1,
                    lessonIDs: 1,
                  }),
                )}
            ></cr-dropdown-search-selection>
            <cr-flex space="md" horizontal crossaxis-center>
              ${this._currentSectionOfLesson.value?.hiddenFromStudents && this._currentSectionOfLesson.value._id
                ? html`<x-icon ${css({ fontSize: '1.6rem', color: this.colors.secondary, cursor: 'pointer' })}>
                    warning
                    <x-tooltip> <cr-text> Der übergeordnete Themenbereich ist bereits versteckt </cr-text> </x-tooltip>
                  </x-icon>`
                : nothing}
              <cr-checkbox ${this._form.field('hiddenFromStudents')} label="Lektion verstecken?"></cr-checkbox>
            </cr-flex>
          </cr-card>

          <cr-card space="md" padding="md">
            <chancen-component-card-title>
              Welche Lektionen zuerst abgeschlossen werden müssen
            </chancen-component-card-title>
            ${!this._lesson.value?._id
              ? html` <cr-text>Die Lektion muss intial gespeichert werden um Abhängigkeiten anzugeben</cr-text> `
              : html` <cr-dropdown-multiselection
                  .value=${this._otherLessonsInSection.value?.filter(lesson =>
                    this._lesson.value?.lessonIDsDependingOn.includes(lesson._id),
                  )}
                  @value-picked="${(e: CustomEvent) => {
                    const newIds = e.detail.value.map(lesson => lesson._id);
                    this._lesson.value?.lessonIDsDependingOn.splice(
                      0,
                      this._lesson.value.lessonIDsDependingOn.length,
                      ...newIds,
                    );
                  }}"
                  placeholder="Lektionen"
                  .selectables=${this._otherLessonsInSection.value?.filter(
                    lesson => lesson._id !== this._lesson.value?._id,
                  )}
                  .renderString=${(item: CampusLesson) => item.name}
                ></cr-dropdown-multiselection>`}
          </cr-card>

          <cr-card space="md" padding="md">
            <campus-component-attachements
              .fileIDs=${this._lesson.value?.attachementIDs ?? []}
              @fileUploaded=${async (e: CustomEvent) => {
                this._lesson.value?.attachementIDs.push(e.detail.fileID);
                await this._lesson.value?.save();
              }}
              @fileDeleted=${async (e: CustomEvent) => {
                if (!this._lesson.value) throw new Error('Lesson not found, this should not happen!');
                const index = this._lesson.value.attachementIDs.findIndex(a => a === e.detail.fileID);
                if (index === -1) throw new Error('Attachement not found in lesson, this should not happen!');
                this._lesson.value.attachementIDs.splice(index, 1);
                await this._lesson.value.save();
              }}
              showDeletion
              showDownload
              showUpload
            ></campus-component-attachements>
          </cr-card>
        </cr-grid>

        <cr-card space="md" padding="md">
          <chancen-component-card-title> Verknüpfte Inhalte </chancen-component-card-title>
          ${this._showDragAndDropWithExercises()}
          ${!this._lesson.value?._id ? nothing : this._showExerciseAddingSection()}
        </cr-card>
      </cr-flex>
    `;
  }

  override header() {
    return html`
      <cr-flex horizontal crossaxis-center end space="md">
        ${this._lesson.value?._id
          ? html`
              <campus-delete-button
                @click=${async () => {
                  if (!(await ChromiumDialog.confirm('Lektion wirklich löschen?'))) return;
                  await this._lesson.value?.remove();

                  void ChromiumSnackbar.show('Lerninhalt gelöscht');
                  goTo(CampusRoutes.lessons);
                }}
              >
              </campus-delete-button>
            `
          : nothing}
        <cr-button
          mode="outline"
          icon="qr_code_2"
          @click=${() =>
            CampusDialogGenerateQrcode.showPopup({
              draggable: this.size.mobile || this.size.tablet,
              props: { qrCodeString: A.absoluteUrl(`${CampusRoutes.play}/${this._lesson.value?._id}`) },
            })}
        >
          QR-Code
        </cr-button>
        <cr-button
          icon="save"
          @click=${async () => {
            this._errorMsg.next('');
            await this._form.validate();
          }}
        >
          ${!this._lesson.value?._id ? 'Erstellen' : 'Speichern'}
        </cr-button>
      </cr-flex>
    `;
  }

  private _showDragAndDropWithExercises() {
    const filteredExercises = this._filterExercises(
      this._exercisesOfLesson.value?.sort((a, b) => a.sortingOrderInLesson - b.sortingOrderInLesson) ?? [],
    );
    return html`
      <cr-input
        @value-changed="${(e: CustomEvent<{ value: string }>) => this._searchText.next(e.detail.value)}"
        placeholder="Suche"
      ></cr-input>
      <x-drag-drop
        degrees-on-move=${0}
        drag-mode=${'column'}
        child-selector=${'.draggable'}
        @resorted=${async (e: CustomEvent) => {
          e.detail.order.forEach(async (exerciseID, index) => {
            const exercise = this._exercisesOfLesson.value?.find(exerciseSub => exerciseSub._id === exerciseID);
            if (!exercise) {
              console.warn(
                'Something went wrong while sorting, this should not happen! Couldtn finx resorted exercise in array',
              );
              return;
            }

            exercise.sortingOrderInLesson = index;
            await exercise.save();
          });
          void ChromiumSnackbar.show('Sortierung wurde gespeichert');
        }}
      >
        <cr-flex ${css({ width: '100%', userSelect: 'none' })} space="sm" padding="md">
          ${!this._lesson.value?._id
            ? html`
                <cr-text> Es muss initial gespeichert werden um Aufgaben der Lektion anzulegen oder zu sehen. </cr-text>
              `
            : filteredExercises.length
            ? html`
                ${repeat(
                  this._filterExercises(
                    this._exercisesOfLesson.value?.sort((a, b) => a.sortingOrderInLesson - b.sortingOrderInLesson) ??
                      [],
                  ),
                  (content, index) => this._showExerciseBoxPreview(content, index),
                )}
              `
            : html` <cr-text>Diese Lektion beinhaltet keine Aufgaben</cr-text> `}
        </cr-flex>
      </x-drag-drop>
    `;
  }

  private _showExerciseBoxPreview(exercise: CampusExercise, index: number) {
    return html`
      <cr-flex
        class="draggable"
        key="${exercise._id}"
        ?horizontal=${!(this.size.mobile || this.size.tablet)}
        space-between
        center
        crossaxis-center
        ${css({
          background: this.colors.neutral.shade(50),
          cursor: 'pointer',
          boxShadow: '0 1px 3px rgba(0,0,0,0.12), 0 1px 2px rgba(0,0,0,0.24)',
        })}
      >
        <cr-flex crossaxis-center padding="sm" space="md">
          <cr-icon ${css({ fontSize: '2rem' })} class="handle">drag_indicator</cr-icon>
        </cr-flex>

        <cr-flex
          ?horizontal=${!(this.size.mobile || this.size.tablet)}
          crossaxis-center
          space="md"
          ${css({ width: '50vw' })}
          @click=${async () => {
            window.open(
              CampusRoutes.excercises +
                `/edit/${exercise.type}?lessonID=${this._lesson.value?._id ?? ''}&exerciseID=${
                  exercise._id
                }&closeOnSave=true`,
              '_blank',
            );
          }}
        >
          ${exercise.type === ExerciseType.H5P
            ? html`
                <cr-text ${css({ width: '20rem' })}>${exercise.h5pContentDesc?.trim()}:</cr-text>
                <cr-text ${css({ marginLeft: '3rem' })}>${exercise.h5pContentTitle?.trim()}</cr-text>
              `
            : html`
                <cr-text ${css({ width: '20rem' })}>${exercise.typeString.trim()}:</cr-text>
                <cr-text ${css({ marginLeft: '3rem' })}>${exercise.title?.substr(0, 40).trim()}...</cr-text>
              `}
        </cr-flex>
        <cr-flex space="sm" ${css({ alignItems: 'flecr-start', marginRight: this.spacing.md })}>
          <chancen-component-context-menu
            .items=${[
              {
                icon: 'content_copy',
                label: 'Duplizieren',
                action: async () => {
                  if (!(await ChromiumDialog.confirm('Aufgabe wirklich duplizieren?'))) return;
                  await ChromiumDialog.guard(async () => {
                    if (!this._lesson.value?._id) {
                      void ChromiumSnackbar.show('Lektion nicht vorhanden!');
                      return;
                    }
                    const duplicateID = await LearningContentAPI.manipulation.exercise.duplicate(
                      exercise._id,
                      this._lesson.value._id,
                    )();
                    if (!duplicateID) {
                      void ChromiumSnackbar.show('Das Duplizieren hat nicht funktioniert!');
                      return;
                    }
                    void ChromiumSnackbar.show('Aufgabe wurde erfolgreich dupliziert');
                    this.requestUpdate();
                  });
                },
              },
              {
                icon: 'double_arrow',
                label: 'Verschieben',
                action: async () => {
                  await CampusDialogMoveExercise.showPopup({
                    draggable: this.size.mobile || this.size.tablet,
                    props: { exerciseToMove: exercise },
                  });
                  void ChromiumSnackbar.show('Aufgabe wurde erfolgreich verschoben');
                },
              },
              {
                icon: 'delete',
                label: 'Löschen',
                action: async () => {
                  if (!(await ChromiumDialog.confirm('Aufgabe wirklich löschen?'))) return;
                  await ChromiumDialog.guard(async () => {
                    await exercise.remove();
                    void ChromiumSnackbar.show('Aufgabe wurde erfolgreich gelöscht');
                  });
                },
              },
            ]}
          ></chancen-component-context-menu>
        </cr-flex>
      </cr-flex>
    `;
  }

  private _showExerciseAddingSection() {
    return html` <cr-flex ${css({ alignItems: 'flex-end' })}>
      <chancen-component-context-menu
        mode="action"
        icon=""
        .items=${Object.values(ALL_EXERCIE_PAGES)
          .sort((a, b) => {
            if (a.exerciseName < b.exerciseName) return -1;
            if (a.exerciseName > b.exerciseName) return 1;
            return 0;
          })
          .map(e => {
            return {
              icon: 'add',
              label: e.exerciseName,
              action: async () => {
                window.open(
                  CampusRoutes.excercises +
                    `/edit/${e.exerciseType}?lessonID=${this._lesson.value?._id ?? ''}&closeOnSave=true`,
                  '_blank',
                );
              },
            };
          })}
      >
        Inhalt Hinzufügen
      </chancen-component-context-menu>
    </cr-flex>`;
  }

  private _filterExercises(localExercises: CampusExercise[]): CampusExercise[] {
    if (!(this._searchText.value || []).length) return localExercises;
    const searchText = this._searchText.value.toLocaleLowerCase();
    return localExercises.filter(con => {
      if (con.questionText?.toLocaleLowerCase().includes(searchText)) return true;
      if (con.questionAnswer?.toLocaleLowerCase().includes(searchText)) return true;
      if (con.h5pContentDesc?.toLocaleLowerCase().includes(searchText)) return true;
      if (con.h5pContentTitle?.toLocaleLowerCase().includes(searchText)) return true;
      if (con.typeString.toLocaleLowerCase().includes(searchText)) return true;
      return false;
    });
  }
}
